import {
  ClipboardList, Clock, User, MapPin, Package 
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Requisition } from '../../types';
import {
  STATUS_LABELS, STATUS_COLORS 
} from '../../types';

interface RequisitionCardProps {
  readonly requisition: Requisition;
  readonly onClick?: () => void;
}

export function RequisitionCard({
  requisition, onClick 
}: RequisitionCardProps) {
  const neededByDate = new Date(requisition.neededBy);
  const isOverdue = neededByDate < new Date() && !['fulfilled', 'rejected'].includes(requisition.status);

  return (
    <button type="button" onClick={onClick} className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{requisition.requisitionId}</h3>
            <p className="flex items-center gap-1 text-sm text-gray-500">
              <MapPin className="h-3 w-3" /> {requisition.venueName}
            </p>
          </div>
        </div>
        <span className={cn('rounded-full px-2 py-1 text-xs font-medium', STATUS_COLORS[requisition.status])}>
          {STATUS_LABELS[requisition.status]}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <User className="h-4 w-4" /> {requisition.requestedByName}
        </span>
        <span className="flex items-center gap-1">
          <Package className="h-4 w-4" /> {requisition.totalItems} items
        </span>
        <span className={cn('flex items-center gap-1', isOverdue && 'text-red-600 font-medium')}>
          <Clock className="h-4 w-4" /> {isOverdue ? 'Overdue' : `Need by ${neededByDate.toLocaleDateString()}`}
        </span>
      </div>

      {requisition.notes && (
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{requisition.notes}</p>
      )}

      <div className="mt-3 space-y-1">
        {requisition.items.slice(0, 3).map((item) => (
          <div key={item.lineId} className="flex items-center justify-between text-sm">
            <span className="text-gray-700">{item.itemName}</span>
            <span className="text-gray-500">
              {item.fulfilledQuantity === undefined ? '' : `${item.fulfilledQuantity}/`}
              {item.approvedQuantity ?? item.requestedQuantity} {item.unit}
            </span>
          </div>
        ))}
        {requisition.items.length > 3 && (
          <p className="text-xs text-gray-400">+{requisition.items.length - 3} more items</p>
        )}
      </div>
    </button>
  );
}
